import { keyValueMirror } from 'store/store-functions';
import { addMessage, clearMessages } from './app';
import { getBase64StringFromDataURL, createBlobFromBase64String } from 'utils/helpers';
import { buildAuthUrl, buildRefreshURL } from 'navigation/navigationHelpers';
import { MESSAGE_TYPES } from 'components/notifications';
import { filterProtectedCreators } from 'components/generic/ProtectedContent';
import constants from 'constants';
import axios from 'axios';

const { ARCHVISION_TOKEN, ARCHVISION_REFRESH_TOKEN } = constants.TOKEN_NAMES;
const { CONTEXT_VALUES } = constants;
const { VITE_API_HOST: apiHost, VITE_API_DEV_HOST } = process.env;
const apiDevHost = VITE_API_DEV_HOST;


export const actions = keyValueMirror(
  'GET_USER_DATA',
  'GET_CREATORS',
  'GET_FREE_MODELS',
  'GET_SELECTED_MODEL',
  'GET_SELECTED_MODELS',
  'GET_USER_MODELS',
  'GET_USER_JOBS',
  'GET_HAS_USER_MODELS',
  'SET_MODELS_LOADING',
  'GET_USER_PURCHASED_MODELS',
  'ADD_TAG_FILTER',
  'ADD_CATEGORY_FILTER',
  'REMOVE_TAGS_FILTER',
  'SET_IS_AUTHORIZING',
  'RESET_IS_AUTHORIZING',
  'CLEAR_TAGS_FILTERS',
  'CLEAR_CATEGORY_FILTER',
  'CLEAR_FILTERS',
  'SET_USER_PREFERENCES_FROM_STORE',
  'SET_USER_PREFERENCES',
  'SET_TEMPORARY_ROLE',
  'GET_SAMPLE_MODELS',
  'GET_TEAM_MODELS',
  'SET_SELECTED_USER_TEAM',
  'RESET_MODELS_DATA',
  'RESET_CREATORS',
  'RESET_FREE_MODELS',
  'RESET_USER_MODELS',
  'RESET_USER_JOBS',
  'RESET_USER_PURCHASED_MODELS',
  'RESET_TEAM_MODELS',
);

export const getNewAccessToken = (refreshToken, clearTokens, saveTokens) => async dispatch => {
  try {
    const url = buildRefreshURL();
    const options = {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
      header: {
        'Content-Type': 'application/json',
      },
    };


    const res = await fetch(url, options);

    if (res.status === 404) {
      clearTokens?.();
      window.location.href = buildAuthUrl();
      throw new Error('Unable to refresh access token.');
    }

    const data = await res.json();

    clearTokens?.();
    saveTokens?.(data.access_token, data.refresh_token);
    dispatch(getUserData(data.access_token, clearTokens, saveTokens));
  } catch (e) {
    const message = e.message || 'Error retrieving new access token.';
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };

    clearTokens?.();
    dispatch(clearMessages());
    dispatch(addMessage([ { message, config } ]));
    window.location.href = buildAuthUrl();
  }
};

export const getUserData = (token, clearTokens, saveTokens) => async dispatch => {
  try {
    const url = `${apiDevHost}/oauth/me`;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Unable to get user data.');
    }

    const payload = await res.json();
    const error = payload.type === 'Error' && payload.errorMessage;
    const refreshToken = localStorage.getItem(ARCHVISION_REFRESH_TOKEN);

    if (payload.active === false && refreshToken) {
      dispatch(
        getNewAccessToken(refreshToken, clearTokens, saveTokens),
      );
      throw new Error('Refreshing session...');
    }

    if ((payload.active === false && !refreshToken) || error) {
      // Invalidate auth and redirect to log in screen if token is invalid.
      dispatch(invalidateAuth(token, clearTokens, 'Unable to authenticate. Redirecting...'));
      throw new Error(error || 'Invalid token.');
    }

    const action = { type: actions.GET_USER_DATA, payload };
    dispatch(action);
  } catch (e) {
    const message = e.message || 'Error retrieving user data';
    const config = { type: MESSAGE_TYPES.warning, timer: 2.5 };

    dispatch(clearMessages());
    dispatch(addMessage([ { message, config } ]));
  }
};


export const sampleModelsGuids = {
  people: [
    '21-29CF-BE8F-C5B0-6C39-1C0C-9250-50CD-2437-00',
    '21-D7B6-9D35-B494-5F9C-8AB6-4D8B-AA4E-059E-00',
    '21-8F37-790C-53C7-AE2B-5C4D-213F-1534-05FD-00',
    '21-2DDE-FE01-16B8-2888-9A6C-A1E9-6776-7A09-00',
    '21-0D7C-BEA2-9F68-D96E-C906-071A-C235-1C0D-00',
    '21-31D1-2901-85FA-18BB-87BF-75C7-B073-03F1-00',
  ],
  transportation: [
    '21-8C64-6C6B-6B5B-2BFE-B362-7D71-1367-BA4B-00',
    '21-C58D-A957-313B-7CEB-5633-38E6-85BB-7F70-00',
    '21-A1F1-7EB1-5251-98B8-9E6C-1DDD-4C1F-0AFC-00',
    '21-4F31-0D79-7112-DCA5-C825-E68A-9123-43D1-00',
    '21-C059-414D-A18F-0FF1-BAF5-8E17-632E-8490-00',
    '21-5097-1ED3-B2D2-0F22-4F9E-DFFD-1BD4-4C73-00',
  ],
  furniture: [
    '21-ACE6-2426-181A-353D-D2A7-0B83-F4E9-F35B-00',
    '21-7B9B-07E0-4D0C-D740-7DB6-9A72-F383-C266-00',
    '21-25DC-A126-1369-3693-BE60-9EF9-8A93-A740-00',
    '21-C5D9-102C-8E36-5464-63E4-C754-8F41-E6D7-00',
    '21-D51E-97FE-AE80-0A2A-B542-10CD-B281-B431-00',
    '21-7426-40F7-B41B-FF0B-9318-D3A5-CD6F-D43A-00',
  ],
  other: [
    '21-C502-DBDB-9A89-B365-E3B9-CFCE-A1F0-C493-00',
    '21-8D8E-2A89-48AB-2079-11A1-797C-D2A3-7905-00',
    '21-B0D7-715C-77C2-83C0-5F76-3AFA-9206-2AEA-00',
    '21-E502-00D2-3BF4-26C6-3BC0-2A74-38F1-859E-00',
    '21-AD24-065D-B2E3-CC56-B3D1-39F3-C5CC-F80F-00',
    '21-5126-6271-366E-5B80-DC79-6737-6A19-8EA0-00',
  ],
  vegetation: [
    '21-2A54-8956-051E-FA79-0C7A-C73E-7C6E-4728-00',
    '21-2A54-8956-051E-FA79-0C7A-C73E-7C6E-4728-00',
    '21-5E75-9745-263A-D861-3BBD-6999-6E61-F268-00',
    '21-B1C3-E769-EE27-7034-E4CA-2084-8AAC-164B-00',
    '21-EF37-E899-E742-BA88-3E2E-9653-D406-FF75-00',
    '21-0597-3F67-A8A4-A289-8685-A8A0-DCDF-C6DE-00',
  ],
};

export const getSampleModels = args => async dispatch => {
  const { onComplete, onError } = args;

  try {
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const allGuids = Object.values(sampleModelsGuids).flat(); // Flatten all GUIDs into a single array

    const results = await Promise.all(allGuids.map(guid => {
      const url = `${apiDevHost}/models/${guid}`;
      return fetch(url, options);
    }));

    if (results.some(res => !res.ok)) {
      throw new Error('Unable to fetch user models.');
    }

    const sampleModels = await Promise.all(results.map(res => res.json()));

    sampleModels.forEach(model => {
      const formats = {};
      model.formats.forEach(format => formats[format.key] = format.availability);
      model.formats = formats;
      model.status = 'COMPLETED';
      model.free_download = 1;
    });

    const action = { type: actions.GET_SAMPLE_MODELS, payload: sampleModels };

    dispatch(action);
    onComplete?.();
  } catch (e) {
    onError?.();
  }
};

export const getUserJobs = args => async dispatch => {
  const { token, onComplete, onError, userId } = args;

  let params = window.location.search;
  if(!params.includes('status')) {
    params += '&status=FAILED&status=QUEUED&status=PROCESSING&status=WAITING_FOR_FILE';
  }

  try {
    const url = `${apiDevHost}/users/${userId}/jobs${params}`;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);

    if (!res.ok) {
      throw new Error('Unable to fetch user models.');
    }

    const payload = await res.json();
    const incompleteJobs = payload.jobs.filter(job => job.status !== 'COMPLETED');

    const { currentPage, pageSize, filteredTotalJobs, statuses, isNextPage } = payload;

    const order = [ 'WAITING_FOR_FILE', 'QUEUED', 'PROCESSING', 'FAILED' ];
    const categories = statuses
      .filter(({ status }) => status !== 'COMPLETED') // Skip COMPLETED
      .map(({ status, count }) => ({
        categories: [],
        id: status,
        name: status,
        totalModels: count,
      }))
      .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));

    const totalCount = categories.reduce((prev, category) => prev += category.totalModels, 0);

    const filteredResults = [
      {
        models: incompleteJobs,
        name: 'nameMainGroup',
        totalModels: incompleteJobs.length,
      },
    ];

    const userJobs = {
      filteredResults: filteredResults,
      tags: [],
      totalModels: totalCount,
      filteredTotalModels: filteredTotalJobs,
      currentPage,
      pageSize,
      isNextPage,
      allPartners: categories,
    };

    const action = { type: actions.GET_USER_JOBS, payload: userJobs };

    dispatch(action);
    onComplete?.();
  } catch (e) {
    onError?.();
    dispatch(
      addMessage(e.message || 'Error retrieving user jobs', {
        type: MESSAGE_TYPES.error,
        timer: 2.5,
      }),
    );
  }
};

export const getUserModels = args => async dispatch => {
  const { token, onComplete, onError, userId } = args;

  const params = window.location.search;

  try {
    const url = `${apiDevHost}/users/${userId}/models${params}`;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Unable to fetch user models.');
    }

    const payload = await res.json();

    const {
      models,
      categoryCounts,
      tags,
      totalModels,
      filteredTotalModels,
      currentPage,
      pageSize,
      isNextPage,
      tempCategoryCounts,
    } = payload;

    models.forEach(model => {
      const formats = {};
      model.formats.forEach(format => formats[format.key] = format.availability);
      model.formats = formats;
      model.status = 'COMPLETED';
    });

    const filteredResults = [
      {
        models,
        name: 'nameMainGroup',
        totalModels: models.length,
      },
    ];

    tempCategoryCounts.forEach(category => {
      category.totalModels = category.count;
      delete category.count;
      category.id = category.name;

      category.subcategories.forEach(subcategory => {
        subcategory.id = `${category.name}_${subcategory.name}`;

        subcategory.totalCategoryModels = subcategory.count;
        subcategory.categoryName = subcategory.name;
        delete subcategory.count;
        delete subcategory.name;
      });

      category.categories = category.subcategories;
      delete category.subcategories;
    });

    categoryCounts.forEach(category => {
      category.totalModels = category.count;
      delete category.count;
      category.id = category.name;

      category.subcategories.forEach(subcategory => {
        subcategory.id = `${category.name}_${subcategory.name}`;

        subcategory.totalCategoryModels = subcategory.count;
        subcategory.categoryName = subcategory.name;
        delete subcategory.count;
        delete subcategory.name;
      });

      category.categories = category.subcategories;
      delete category.subcategories;
    });

    const userModels = {
      filteredResults,
      tags,
      totalModels,
      filteredTotalModels,
      currentPage,
      pageSize,
      isNextPage,
      allPartners: tempCategoryCounts,
    };

    const action = { type: actions.GET_USER_MODELS, payload: userModels };

    dispatch(action);
    onComplete?.();
  } catch (e) {
    onError?.();
    dispatch(
      addMessage(e.message || 'Error retrieving user models', {
        type: MESSAGE_TYPES.error,
        timer: 2.5,
      }),
    );
  }
};

export const getUserPurchasedModels = args => async dispatch => {
  const { token, onComplete, onError, userId } = args;

  const params = window.location.search;

  try {
    const url = `${apiDevHost}/users/${userId}/models/purchased${params}`;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Unable to fetch user models.');
    }

    const payload = await res.json();

    const {
      models, categoryCounts, tags,
      totalModels, filteredTotalModels, currentPage,
      pageSize, tempCategoryCounts,
    } = payload;

    models.forEach(model => {
      const formats = {};
      model.formats.forEach(format => formats[format.key] = format.availability);
      model.formats = formats;
      model.status = 'COMPLETED';
    });

    const filteredResults = [
      {
        models,
        name: 'nameMainGroup',
        totalModels: models.length,
      },
    ];

    tempCategoryCounts.forEach(category => {
      category.totalModels = category.count;
      delete category.count;
      category.id = category.name;

      category.subcategories.forEach(subcategory => {
        subcategory.id = `${category.name}_${subcategory.name}`;

        subcategory.totalCategoryModels = subcategory.count;
        subcategory.categoryName = subcategory.name;
        delete subcategory.count;
        delete subcategory.name;
      });

      category.categories = category.subcategories;
      delete category.subcategories;
    });

    categoryCounts.forEach(category => {
      category.totalModels = category.count;
      delete category.count;
      category.id = category.name;

      category.subcategories.forEach(subcategory => {
        subcategory.id = `${category.name}_${subcategory.name}`;

        subcategory.totalCategoryModels = subcategory.count;
        subcategory.categoryName = subcategory.name;
        delete subcategory.count;
        delete subcategory.name;
      });

      category.categories = category.subcategories;
      delete category.subcategories;
    });

    const userModels = {
      filteredResults,
      tags,
      totalModels,
      filteredTotalModels,
      currentPage,
      pageSize,
      allPartners: tempCategoryCounts,
    };

    const action = { type: actions.GET_USER_PURCHASED_MODELS, payload: userModels };

    dispatch(action);
    onComplete?.();
  } catch (e) {
    onError?.();
    dispatch(
      addMessage(e.message || 'Error retrieving user purchased models', {
        type: MESSAGE_TYPES.error,
        timer: 2.5,
      }),
    );
  }
};

export const getTeamModels = args => async dispatch => {
  const { teamId, token, onError, onComplete } = args;


  const params = window.location.search;

  try {
    const url = `${apiDevHost}/teams/${teamId}/models${params}`;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Unable to fetch user models.');
    }

    const payload = await res.json();

    const {
      models, categoryCounts, tags,
      totalModels, filteredTotalModels, currentPage,
      pageSize, isNextPage,
    } = payload;

    models.forEach(model => {
      const formats = {};
      model.formats.forEach(format => formats[format.key] = format.availability);
      model.formats = formats;
      model.status = 'COMPLETED';
    });

    const filteredResults = [
      {
        models,
        name: 'nameMainGroup',
        totalModels: models.length,
      },
    ];

    categoryCounts.forEach(category => {
      category.totalModels = category.count;
      delete category.count;
      category.id = category.name;

      category.subcategories.forEach(subcategory => {
        subcategory.id = `${category.name}_${subcategory.name}`;

        subcategory.totalCategoryModels = subcategory.count;
        subcategory.categoryName = subcategory.name;
        delete subcategory.count;
        delete subcategory.name;
      });

      category.categories = category.subcategories;
      delete category.subcategories;
    });

    const userModels = {
      filteredResults,
      tags,
      totalModels,
      filteredTotalModels,
      currentPage,
      pageSize,
      isNextPage,
      allPartners: categoryCounts,
    };

    const action = { type: actions.GET_TEAM_MODELS, payload: userModels };

    dispatch(action);
    dispatch(setSelectedUserTeam(teamId));
    onComplete?.();
  } catch (e) {
    onError?.();
    dispatch(
      addMessage(e.message || 'Error retrieving team models', {
        type: MESSAGE_TYPES.error,
        timer: 2.5,
      }),
    );
  }

};

export const getSelectedModel = args => async dispatch => {
  const { token, onComplete, onError, id, userId, teamId } = args;

  try {
    let url;
    const onCreators = window.location.search.includes(CONTEXT_VALUES.CREATORS);
    const onTeamModels = window.location.search.includes(CONTEXT_VALUES.TEAM);
    const onPurchasedModels = window.location.search.includes(CONTEXT_VALUES.PURCHASED);
    const onAdmin = window.location.search.includes(CONTEXT_VALUES.ADMIN);

    if (onCreators) {
        url = `${apiDevHost}/models/${id}`;
    } else if (onTeamModels) {
        url = `${apiDevHost}/teams/${teamId}/models?page=1&per_page=1&search=${id}`;
    }
    else if (onPurchasedModels) {
      url = `${apiDevHost}/users/${userId}/models/purchased?page=1&per_page=1&search=${id}`;
    }
    else if (onAdmin) {
      const baseUrl = VITE_API_DEV_HOST;
      url = `${baseUrl}/admin/models?page=1&per_page=1&search=${id}`;
    }
     else {
      url = `${apiDevHost}/users/${userId}/models?page=1&per_page=1&search=${id}`;
    }

    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const options = {
      method: 'GET',
      headers,
    };

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Unable to fetch creator content.');
    }

    const response = await res.json();
    const model =  onCreators ? response : response.models.find(model => model.rpc_guid === id);

    if (!model) {
      throw new Error('Model not found.');
    }

    // Convert formats array to object
    const formats = {};
    model.formats.forEach(format => {
      formats[format.key] = format.availability;
    });
    model.formats = formats;

    dispatch({
      type: actions.GET_SELECTED_MODEL,
      payload: model,
    });

    onComplete?.();
  } catch (e) {
    onError?.();
    dispatch(
      addMessage(e.message || 'Error retrieving selected model data', {
        type: MESSAGE_TYPES.error,
        timer: 2.5,
      }),
    );
  }
};


export const getSelectedModels = args => async dispatch => {
  const {
    token,
    onComplete,
    onError,
    userId,
    teamId,
    creator,
    category,
    subcategory,
  } = args;

  try {
    let url;
    const onCreators = window.location.search.includes(CONTEXT_VALUES.CREATORS);
    const onTeamModels = window.location.search.includes(CONTEXT_VALUES.TEAM);
    const onPurchasedModels = window.location.search.includes(CONTEXT_VALUES.PURCHASED);

    const params = new URLSearchParams({
      page: 1,
      per_page: 5,
    });

    if (category) {
      params.append('search_category', category);
    }

    if (subcategory) {
      params.append('search_subcategory', subcategory);
    }

    if (onCreators) {
      params.append('creators', creator);
      url = `${apiDevHost}/models?${params.toString()}`;
    } else if (onTeamModels) {
      url = `${apiDevHost}/teams/${teamId}/models?${params.toString()}`;
    } else if (onPurchasedModels) {
      url = `${apiDevHost}/users/${userId}/models/purchased?${params.toString()}`;
    } else {
      url = `${apiDevHost}/users/${userId}/models?${params.toString()}`;
    }

    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const options = {
      method: 'GET',
      headers,
    };

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Unable to fetch creator content.');
    }

    const response = await res.json();
    const models = response.models;

    if (!models) {
      throw new Error('Model not found.');
    }

    models.forEach(model => {
      const formats = {};
      model.formats.forEach(format => {
        formats[format.key] = format.availability;
      });
      model.formats = formats;
    });

    dispatch({
      type: actions.GET_SELECTED_MODELS,
      payload: models,
    });

    onComplete?.();
  } catch (e) {
    onError?.();
    dispatch(
      addMessage(e.message || 'Error retrieving selected models data', {
        type: MESSAGE_TYPES.error,
        timer: 2.5,
      }),
    );
  }
};

export const getHasUserModels = args => async dispatch => {
  const { token, onComplete, onError, userId } = args;

  try {
    const url = `${apiDevHost}/users/${userId}/models?page=1&per_page=1`;
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);

    if (!res.ok) {
      throw new Error('Unable to fetch user models.');
    }

    const payload = await res.json();
    const hasUserModels = !!payload.models.length;

    const action = { type: actions.GET_HAS_USER_MODELS, payload: hasUserModels };

    dispatch(action);
    onComplete?.();
  } catch (e) {
    onError?.();
    dispatch(
      addMessage(e.message || 'Error retrieving user models', {
        type: MESSAGE_TYPES.error,
        timer: 2.5,
      }),
    );
  }
};

export const getFreeModels = args => async dispatch => {
  const { token, onComplete, onError } = args;

  try {
    const url = `${apiDevHost}/models?page=1&per_page=50&attributes=free_download`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const options = {
      method: 'GET',
      headers,
    };

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Unable to fetch creator content.');
    }

    const responce = await res.json();
    const {
      models,
    } = responce;

    models.forEach(model => {
      const formats = {};
      model.formats.forEach(format => formats[format.key] = format.availability);
      model.formats = formats;
    });

    const action = {
      type: actions.GET_FREE_MODELS,
      payload: models,
    };
    dispatch(action);
    onComplete?.();
  } catch (e) {
    onError?.();
    dispatch(
      addMessage(e.message || 'Error retrieving creator models', {
        type: MESSAGE_TYPES.error,
        timer: 2.5,
      }),
    );
  }
};

export const getCreators = args => async dispatch => {
  const { token, onComplete, onError, userRoles } = args;

  let params = window.location.search;
  // TODO: Fix replace per page by categories in search.
  if (!params.includes('per_page')) {
    params += '&per_page=50';
  }

  try {
    const url = `${apiDevHost}/models${params}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const options = {
      method: 'GET',
      headers,
    };

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Unable to fetch creator content.');
    }

    const responce = await res.json();
    const {
      models,
      totalModels,
      categoryCounts,
      tempCategoryCounts,
      filteredTotalModels,
      currentPage,
      pageSize,
      tags,
      isNextPage,
    } = responce;

    models.forEach(model => {
      const formats = {};
      model.formats.forEach(format => formats[format.key] = format.availability);
      model.formats = formats;
    });

    const groupedModels = models.reduce((acc, model) => {
      const partner = acc[model.creator];
      if (partner) {
        partner.modelCount += 1;
        partner.models.push(model);

        const category = partner.categories.find(category => category.name === model.category);
        if (category) {
          category.categoryModelCount += 1;
        } else {
          partner.categories.push({
            name: model.category,
            categoryModelCount: 1,
          });
        }

        return acc;
      }

      acc[model.creator] = {
        partnerName: model.creator,
        modelCount: 1,
        models: [ model ],
        categories: [
          {
          name: model.category,
          categoryModelCount: 1,
          },
        ],
      };

      return acc;
    }, {});
    const filteredResults = Object.values(groupedModels);

    const tagsByPartner = filteredResults.map(partner => {
      const tagsSet = new Set(partner.models.flatMap(model => model.tags));
      return {
        name: partner.partnerName,
        tags: Array.from(tagsSet.keys()),
      };
    });

    const creatorNames = categoryCounts.map(cat => {
      return {
        name: cat.creator,
        totalModels: cat?.categories.reduce((acc, item) => acc += item.count, 0),
      };
    });

    categoryCounts.forEach(category => {
      category.totalModels = 0;
      category.categories.forEach(subcategory => {
        category.totalModels += subcategory.count;
        subcategory.id = `${category.creator}_${subcategory.name}`;

        subcategory.totalCategoryModels = subcategory.count;
        subcategory.categoryName = subcategory.name;
        delete subcategory.count;
        delete subcategory.name;
      });

      category.name = category.creator;
      delete category.creator;
    });

    tempCategoryCounts.forEach(category => {
      category.totalModels = category.count;
      delete category.count;
      category.id = category.name;

      category.subcategories.forEach(subcategory => {
        subcategory.id = `${category.name}_${subcategory.name}`;

        subcategory.totalCategoryModels = subcategory.count;
        subcategory.categoryName = subcategory.name;
        delete subcategory.count;
        delete subcategory.name;
      });

      category.categories = category.subcategories;
      delete category.subcategories;
    });

    const payload = {
      tags,
      filteredResults,
      totalModels,
      tagsByPartner,
      filteredTotalModels,
      currentPage,
      pageSize,
      isNextPage,
      allPartners: tempCategoryCounts,
      creatorNames,
    };
    let roleFilteredData = {};

    if (userRoles) {
      // NOTE: Only for initial load when no params are present.
      // to make sure user is not seeing content they don't have permission to access.
      const allPartners = filterProtectedCreators(payload.allPartners, userRoles);
      const filteredResults = filterProtectedCreators(payload.filteredResults, userRoles);
      const tagsByPartner = payload.tagsByPartner;

      roleFilteredData = {
        ...payload,
        allPartners,
        filteredResults,
        tags: allPartners
          .map(partner => {
            const name = partner.name.toLowerCase();
            const tags = tagsByPartner.find(item => item.name.toLowerCase() === name)?.tags;
            return tags;
          })
          .flat()
          .filter(item => item),
        totalModels: allPartners.reduce((prev, current) => prev += current.totalModels, 0),
      };
    }

    const action = {
      type: actions.GET_CREATORS,
      payload: userRoles ? roleFilteredData : payload,
    };

    dispatch(action);
    onComplete?.();
  } catch (e) {
    onError?.();
    dispatch(
      addMessage(e.message || 'Error retrieving creator models', {
        type: MESSAGE_TYPES.error,
        timer: 2.5,
      }),
    );
  }
};

export const invalidateAuth = (token, clearTokens, customMessage) => async dispatch => {
  try {
    const url = `${apiHost}/oauth/v1/invalidate`;
    const options = {
      method: 'POST',
      body: JSON.stringify({
        redirect_uri: buildAuthUrl(),
        access_token: token,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    };
    const message = customMessage || 'Logging out...';
    const config = {
      type: MESSAGE_TYPES.loading,
      showModal: true,
      closeButton: false,
    };

    dispatch(addMessage([ { message, config } ]));

    const res = await fetch(url, options);

    if (res.status === 404) {
      throw new Error('Invalidation call path not found.');
    }
  } catch (e) {
    const message = e.message || 'Error invalidating auth';
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };

    dispatch(clearMessages());
    dispatch(addMessage([ { message, config } ]));
  } finally {
    dispatch(resetIsAuthorizing());
    clearTokens?.();
    localStorage.removeItem(ARCHVISION_TOKEN);
    localStorage.removeItem(ARCHVISION_REFRESH_TOKEN);
    window.location.href = buildAuthUrl();
  }
};

export const addCategoryFilter = category => ({
  type: actions.ADD_CATEGORY_FILTER,
  payload: category,
});

export const addTagFilter = tag => ({
  type: actions.ADD_TAG_FILTER,
  payload: tag,
});

export const removeTagFilter = tag => ({
  type: actions.REMOVE_TAGS_FILTER,
  payload: tag,
});

export const resetModelsData = () => ({ type: actions.RESET_MODELS_DATA });
export const resetCreators = () => ({ type: actions.RESET_CREATORS });
export const resetFreeModels = () => ({ type: actions.RESET_FREE_MODELS });
export const resetUserModels = () => ({ type: actions.RESET_USER_MODELS });
export const resetUserJobs = () => ({ type: actions.RESET_USER_JOBS });
export const resetUserPurchasedModels = () => ({ type: actions.RESET_USER_PURCHASED_MODELS });
export const resetTeamModels = () => ({ type: actions.RESET_TEAM_MODELS });

export const clearCategoryFilter = () => ({
  type: actions.CLEAR_CATEGORY_FILTER,
});

export const clearTagsFilters = () => ({
  type: actions.CLEAR_TAGS_FILTERS,
});

export const clearFilters = () => ({
  type: actions.CLEAR_FILTERS,
});

export const setIsAuthorizing = () => ({
  type: actions.SET_IS_AUTHORIZING,
});

export const resetIsAuthorizing = () => ({
  type: actions.RESET_IS_AUTHORIZING,
});

export const setUserPreferences = value => ({
  type: actions.SET_USER_PREFERENCES,
  payload: value,
});

export const setUserPreferencesFromStore = () => {
  const value = localStorage.getItem(constants.FOV_USER_PREFERENCES);
  return {
    type: actions.SET_USER_PREFERENCES_FROM_STORE,
    payload: value ? JSON.parse(value) : {},
  };
};

export const setTemporaryRole = payload => ({
  type: actions.SET_TEMPORARY_ROLE,
  payload,
});

export const updateModelPreviewImage = args => async dispatch => {
  const { token, guid, payload, onComplete } = args;

  try {
    const url = `${apiDevHost}/models/${guid}/preview`;
    const options = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    };

    const res = await fetch(url, options);
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.message || 'Unable to update metadata.');
    }

    const previewImage = payload?.previewImage;
    const base64String = getBase64StringFromDataURL(previewImage);
    const blob = createBlobFromBase64String(base64String);
    const previewUpdateUrl = data?.putURL;
    const previewUpdateOptions = {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    };

    if (base64String) {
      await axios.put(previewUpdateUrl, blob, previewUpdateOptions);
    }

    // Refetching here would use the preview page's query string; clear instead so My Files reloads with its own.
    dispatch(resetUserModels());
  } catch (e) {
    const message = e.message || 'Error updating preview image';
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
    dispatch(addMessage([ { message, config } ]));
  } finally {
    onComplete?.();
  }
};

export const updateModelMetadata = args => async dispatch => {
  const { token, guid, payload, onComplete } = args;

  try {
    const url = `${apiDevHost}/models/${guid}`;
    const options = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    };

    const res = await fetch(url, options);
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.message || 'Unable to update metadata.');
    }

    dispatch(resetUserModels());
  } catch (e) {
    const message = e.message || 'Error updating model metadata';
    const config = { type: MESSAGE_TYPES.error, timer: 2.5 };
    dispatch(addMessage([ { message, config } ]));
  } finally {
    onComplete?.();
  }
};

export const setSelectedUserTeam = teamId => ({
    type: actions.SET_SELECTED_USER_TEAM,
    payload: teamId,
});
